import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTransferPatientsData } from '@/hooks/patient/useTransferPatientsData.ts';
import { useNavigate, useLocation } from 'react-router-dom';
import UnifiedHeader from '@/components/common/UnifiedHeader';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface TransferPatientsPageProps {
  onBack?: () => void;
  onPatientSelect?: (patientClinicDataId: number, clinicId?: number) => void;
}

const TransferPatientsPage = ({ onBack, onPatientSelect }: TransferPatientsPageProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    sortedNotifications,
    isLoading
  } = useTransferPatientsData();

  // Check navigation state for proper back navigation
  const from = location.state?.from;
  const returnTo = location.state?.returnTo;

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);

  // Calculate pagination values
  const { paginatedNotifications, totalPages, startIndex, endIndex } = useMemo(() => {
    const startIdx = (currentPage - 1) * recordsPerPage;
    const endIdx = startIdx + recordsPerPage;
    const paginated = sortedNotifications.slice(startIdx, endIdx);
    const totalPgs = Math.ceil(sortedNotifications.length / recordsPerPage);

    return {
      paginatedNotifications: paginated,
      totalPages: totalPgs,
      startIndex: startIdx,
      endIndex: endIdx
    };
  }, [sortedNotifications, currentPage, recordsPerPage]);

  // Handle pagination
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleRecordsPerPageChange = (newSize: number) => {
    setRecordsPerPage(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handlePatientSelect = (patientClinicDataId: number) => {
    if (onPatientSelect) {
      onPatientSelect(patientClinicDataId);
    } else {
      // Navigate to patient details page with state indicating it came from transfer patients
      navigate(`/patient-details/${patientClinicDataId}`, {
        state: { fromTransferPatients: true }
      });
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      // Check if we have navigation state indicating where to return
      if ((from === 'cs-dashboard' || from === 'ca-dashboard') && returnTo) {
        navigate(returnTo);
      } else {
        navigate(-1); // Fallback to browser back
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">

      {/* Main Content */}
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Transfer Patient List ({sortedNotifications.length})
          </h2>
          <p className="text-gray-600">Review and accept patient transfers assigned to you</p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="text-gray-600">Loading transfer patients...</span>
            </div>
          </div>
        ) : sortedNotifications.length === 0 ? (
          <div className="text-center py-16">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Clock className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Transfer Patients</h3>
            <p className="text-gray-500">There are currently no patients waiting for transfer acceptance.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-3 gap-4 bg-purple-50 px-6 py-4 font-semibold text-gray-700 border-b">
              <div>Patient Name</div>
              <div>Clinic Name</div>
              <div>Assigned User</div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-100">
              {paginatedNotifications.map((patient, index) => (
                <div
                  key={patient.id}
                  className={`grid grid-cols-3 gap-4 px-6 py-4 hover:bg-purple-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                    }`}
                >
                  <div className="font-medium text-gray-900">{patient.patient_name}</div>
                  <div className="text-gray-700">{patient.clinic_name}</div>
                  <div className="text-gray-700">{patient.assigned_by_user_name || 'Not Assigned'}</div>
                </div>
              ))}
            </div>

            {/* Pagination Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-700">Rows per page:</div>
                <Select value={recordsPerPage.toString()} onValueChange={(value) => handleRecordsPerPageChange(parseInt(value))}>
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-700">
                  {startIndex + 1}-{Math.min(endIndex, sortedNotifications.length)} of {sortedNotifications.length}
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    variant="outline"
                    size="sm"
                    className="flex items-center space-x-1"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span>Previous</span>
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    size="sm"
                    className="flex items-center space-x-1"
                  >
                    <span>Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransferPatientsPage;